import { Trans, useLingui } from "@lingui/react/macro";
import { ORPCError } from "@orpc/client";
import type { Bot } from "@rakazo/contracts";
import { BOT_INSTRUCTIONS_MAX_LENGTH } from "@rakazo/contracts";
import { Button, Disclosure, Textarea } from "@rakazo/ui-web";
import { Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web/components/ui/popover";
import { History } from "lucide-react";
import { useId, useState } from "react";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

export function InstructionSettings({
  bot,
  heading = true,
}: {
  bot: Bot;
  /** A tab already names the field; keep the label for assistive tech only. */
  heading?: boolean;
}) {
  const { t } = useLingui();
  const id = useId();
  const [previousBot, setPreviousBot] = useState(bot);
  const [current, setCurrent] = useState(bot);
  const [baseline, setBaseline] = useState(bot.instructions);
  const [instructions, setInstructions] = useState(bot.instructions);
  // Long instructions start clamped; reading or editing the full text is one
  // press or one focus away.
  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState("");
  if (bot !== previousBot) {
    setPreviousBot(bot);
    if (instructions === baseline) {
      setInstructions(bot.instructions);
      setBaseline(bot.instructions);
    }
    setCurrent(bot);
  }
  async function save(action: () => Promise<Bot>) {
    setPending(true);
    setError("");
    try {
      const updated = await action();
      setConflict(false);
      setCurrent(updated);
      setInstructions(updated.instructions);
      setBaseline(updated.instructions);
    } catch (err) {
      setError(errorText(err));
      setConflict((current) => current || (err instanceof ORPCError && err.code === "CONFLICT"));
    } finally {
      setPending(false);
    }
  }
  const long = instructions.length > 480 || instructions.split("\n").length > 9;
  const collapsed = long && !expanded;
  const history = current.instructionHistory?.length ? (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t`Instruction history`}
            title={t`Instruction history`}
          />
        }
      >
        <History className="size-4 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-96 overflow-y-auto">
        {current.instructionHistory.map((version) => (
          <div key={version.id} className="mt-2 text-sm">
            <Disclosure
              summary={
                <>
                  {new Date(version.createdAt).toLocaleString()} · {version.reason}
                </>
              }
            >
              <pre className="whitespace-pre-wrap">{version.instructions}</pre>
            </Disclosure>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() =>
                void save(() =>
                  rpc.bots.restoreInstructions({
                    botId: bot.id,
                    versionId: version.id,
                    expectedInstructions: current.instructions,
                  }),
                )
              }
            >
              <Trans>Restore</Trans>
            </Button>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  ) : null;
  const label = (
    <label
      htmlFor={`${id}-instructions`}
      className={heading ? "text-xs font-medium text-muted-foreground" : "sr-only"}
    >
      <Trans>Instructions</Trans>
    </label>
  );
  return (
    <div className="space-y-2">
      {heading || history ? (
        <div className="flex min-h-9 items-center justify-between gap-2 pe-1">
          {label}
          {history}
        </div>
      ) : (
        label
      )}
      <div
        data-testid="instructions-preview"
        data-collapsed={collapsed ? "true" : "false"}
        className={collapsed ? "relative max-h-44 overflow-hidden" : "relative"}
      >
        <Textarea
          id={`${id}-instructions`}
          className="font-normal min-h-20 resize-none field-sizing-content text-foreground rounded-xl border-transparent bg-background p-4 shadow-none"
          value={instructions}
          maxLength={BOT_INSTRUCTIONS_MAX_LENGTH}
          disabled={pending}
          onChange={(event) => setInstructions(event.target.value)}
          onFocus={() => setExpanded(true)}
          onBlur={() => {
            if (!conflict && instructions !== current.instructions)
              void save(() =>
                rpc.bots.update({ botId: bot.id, instructions, expectedInstructions: baseline }),
              );
          }}
        />
        {collapsed ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 rounded-b-xl bg-gradient-to-t from-background via-background/80 to-transparent"
          />
        ) : null}
      </div>
      {long ? (
        <Button
          type="button"
          variant="text"
          size="sm"
          className="min-h-11 text-sm"
          aria-expanded={expanded}
          aria-controls={`${id}-instructions`}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? <Trans>Show less</Trans> : <Trans>Show more</Trans>}
        </Button>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {conflict ? (
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => void save(() => rpc.bots.get({ botId: bot.id }))}
        >
          <Trans>Reload</Trans>
        </Button>
      ) : null}
    </div>
  );
}
